import React, { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import ProfileCard from "./ProfileCard";
import ProfileContext from "./ProfileContext";
import useUsers from "../../hooks/useUsers";
import useCompanies from "../../hooks/useCompanies";
import { useNavigate, useSearchParams } from "react-router-dom";

import backgroundImage from "../../assets/profile/background.png";

import "../../styles/profile.scss";
import Projects from "./Projects";
import Reviews from "./Reviews";
import Edit from "./Edit";
import AppContext from "../../AppContext";
import Followers from "./Followers";
import Followings from "./Followings";
import Likes from "./Likes";
import Collections from "./Collections";
import Members from "./Members";
import HeaderFollowing from "./HeaderFollowing";
import ProjectOwner from "./ProjectOwner";
import { toast } from "react-toastify";
import ContentLoader from "react-content-loader";
import { langComponent } from "../../constants";
import ScrollToTop from "../common/ScrollToTop";
import _ from "lodash";
import ChatSessionContext from "../../contexts/ChatSessionContext";
import Purchases from "./Purchases";
import Tenderings from "./Tenderings";

const assetsURI = import.meta.env.ASSETS_URI;

const TABS = {
  MEMBERS: 0,
  PROJECTS: 1,
  TENDERINGS: 2,
  LIKES: 3,
  COLLECTIONS: 4,
  FOLLOWERS: 5,
  FOLLOWINGS: 6,
  REVIEWS: 7,
  PURCHASES: 8,
};

const availableTabs = [
  {
    value: TABS.PROJECTS,
    name: "Projects",
    type: "all",
  },
  {
    value: TABS.TENDERINGS,
    name: "Tenderings",
    type: "all",
  },
  {
    value: TABS.MEMBERS,
    name: "Members",
    type: "company",
  },
  {
    value: TABS.LIKES,
    name: "Likes",
    type: "all",
  },
  {
    value: TABS.FOLLOWERS,
    name: "Followers",
    type: "all",
  },
  {
    value: TABS.FOLLOWINGS,
    name: "Following",
    type: "all",
  },
  {
    value: TABS.REVIEWS,
    name: "Reviews",
    type: "all",
  },
  {
    value: TABS.PURCHASES,
    name: "Purchases",
    type: "all",
  },
];

const Profile = ({
  id,
  isCompany,
  tab,
  onChangeTab,
  onEdit,
  edit,
  onNewProject,
  viewMode,
}) => {
  const [tabs, setTabs] = useState(undefined);
  const [user, setUser] = useState();
  const [plan, setPlan] = useState();
  const [company, setCompany] = useState();
  const [subscriptionStatus, setSubscriptionStatus] = useState(undefined);
  const {
    queryUsers,
    getPlan,
    querySubscriptions,
    patchSubscription,
    createSubscription,
  } = useUsers();
  const { getCompany } = useCompanies();
  const {
    isAuth,
    user: loggedInUser,
    toggleAuthModal,
    getLang,
  } = useContext(AppContext);
  const Translation = getLang(langComponent.profile);
  const [tabSelected, setTabSelected] = useState(undefined);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { startChat } = useContext(ChatSessionContext);

  useEffect(() => {
    if (!isAuth && (!id || id === "profile")) {
      navigate("/");
      return;
    }

    setUser(undefined);
    fetchData();
    return () => {};
  }, [id, isCompany, viewMode]);

  useEffect(() => {
    if (!user) return;
    if (user._id === loggedInUser?._id) return;

    if (searchParams.get("chat") !== null && isAuth) {
      startChat(user._id, loggedInUser);
    }

    setTabs((tabs) =>
      tabs.filter((t) => t.value !== TABS.LIKES && t.value !== TABS.PURCHASES)
    );
  }, [user, isAuth, searchParams]);

  const fetchData = async () => {
    // * Tab handling
    let _tabs = _.cloneDeep(availableTabs);

    // * Company
    if (isCompany) {
      const _company = await getCompany(id);
      if (_company?._id !== loggedInUser?.company) {
        const { data: subscriptions } = await querySubscriptions({
          createdBy: loggedInUser?._id,
          company: id,
        });

        if (subscriptions.length === 0) {
          setSubscriptionStatus(undefined);
        }

        if (subscriptions.length > 0) {
          setSubscriptionStatus(subscriptions[0]);
        }
      }

      // * Remove member & likes tab if logged in user is not in the company
      if (viewMode === "guest" || loggedInUser?.company !== id) {
        _tabs = _tabs.filter(
          (t) =>
            t.value !== TABS.MEMBERS &&
            t.value !== TABS.LIKES &&
            t.value !== TABS.PURCHASES
        );
      }

      setTabs(_tabs);
      setTabSelected(_tabs[0].value);
      setCompany(_company);
      return;
    }

    // * Users
    let filters = {};

    if (!id || id === "profile") {
      filters["_id"] = loggedInUser._id;
    }

    if (id) {
      filters["uri"] = id;
    }

    const { data: users } = await queryUsers(filters);

    if (users.length === 0) {
      toast.error("User not found");
      navigate("/404");
      return;
    }

    const _user = users[0];

    const _plan = await getPlan(_user.plan);

    // * Check if we're subscribed to this user
    if (_user?._id !== loggedInUser?._id) {
      const _subscriptionFilter = {
        createdBy: loggedInUser?._id,
        subscribes: _user?._id,
      };

      if (loggedInUser?.company) {
        _subscriptionFilter.company = loggedInUser?.company;
      }

      const { data: subscriptions } = await querySubscriptions(
        _subscriptionFilter
      );

      if (subscriptions.length === 0) {
        setSubscriptionStatus(undefined);
      }

      if (subscriptions.length > 0) {
        setSubscriptionStatus(subscriptions[0]);
      }
    }

    if (_user.company) {
      const _company = await getCompany(_user.company);
      _user.company = _company;
    }

    if (isCompany) {
    }

    if (!isCompany) {
      // * Remove tabs that are company specific
      _tabs = _tabs.filter((t) => t.type !== "company");

      // * Remove member & likes tab if logged in user is not in the company
      if (_user.company) {
        _tabs = _tabs.filter((t) => t.value !== TABS.PROJECTS);
        _tabs = _tabs.filter((t) => t.value !== TABS.TENDERINGS);
      }
    }

    // * Remove likes tab if viewing someone else's profile
    if (viewMode === "guest") {
      _tabs = _tabs.filter((t) => t.value !== TABS.LIKES);
    }

    setTabs(_tabs);
    setTabSelected(_tabs[0].value);
    setUser(_user);
    setPlan(_plan);
  };

  const handleFollow = async () => {
    // * Display login if not logged in
    if (!isAuth) {
      toggleAuthModal(true, "signin");
      return;
    }

    if (!subscriptionStatus) {
      let _subscription = await createSubscription({
        subscribes: isCompany ? company._id : user?._id,
        company: loggedInUser?.company ?? null,
      });

      setSubscriptionStatus(_subscription);
      return;
    }

    // * Patch subscription
    let _subscription = await patchSubscription(subscriptionStatus._id, {
      active: true,
    });

    setSubscriptionStatus(_subscription);
  };

  const handleUnfollow = async () => {
    let _subscription = await patchSubscription(subscriptionStatus._id, {
      active: false,
    });

    setSubscriptionStatus(_subscription);
  };

  const _getIsOwner = () => {
    if (viewMode === "guest") return false;

    if (!isCompany) {
      return user?._id === loggedInUser?._id;
    }

    return company?._id === loggedInUser?.company;
  };

  // * Context Values
  const contextValues = useMemo(
    () => ({
      isCompany,
      company,
      user,
      currentPlan: plan,
      onEdit,
      setUser,
      onNewProject,
      isOwner: _getIsOwner(),
      isOwnerCompany: company?._id === loggedInUser?.company,
      follow: handleFollow,
      unfollow: handleUnfollow,
      isSubscribed: subscriptionStatus ? subscriptionStatus.active : false,
      viewMode,
      tabs,
      setTabSelected,
    }),
    [user, company, plan, onEdit, loggedInUser, subscriptionStatus, tabs]
  );

  const getContent = () => {
    if (tabSelected === TABS.MEMBERS) {
      return <Members id={id} />;
    }

    if (tabSelected === TABS.PROJECTS) {
      const owner = _getIsOwner();
      if (!owner) {
        return <Projects id={id} isCompany={isCompany} />;
      } else {
        return <ProjectOwner />;
      }
    }

    if (tabSelected === TABS.TENDERINGS) {
      return <Tenderings />;
    }

    if (tabSelected === TABS.LIKES) {
      return <Likes />;
    }

    if (tabSelected === TABS.COLLECTIONS) {
      return <Collections />;
    }

    if (tabSelected === TABS.FOLLOWERS) {
      return <Followers />;
    }

    if (tabSelected === TABS.FOLLOWINGS) {
      return <Followings />;
    }

    if (tabSelected === TABS.REVIEWS) {
      return <Reviews />;
    }

    if (tabSelected === TABS.PURCHASES) {
      return <Purchases />;
    }
  };

  const handleTabClick = (tab) => {
    setTabSelected(tab);
  };

  const handleExitGuestModeClick = () => {
    navigate("/profile");
  };

  const getBanner = () => {
    if ((isCompany && !company) || (!isCompany && !user)) {
      return (
        <div className="profile-banner">
          <ContentLoader width={"100%"} height={320}>
            <rect x="0" y="0" rx="0" ry="0" width="100%" height="100%" />
          </ContentLoader>
        </div>
      );
    }

    if (isCompany)
      return (
        <img
          className="profile-banner"
          src={`${assetsURI}/${company?.banner}`}
          onError={({ target }) => {
            target.src = backgroundImage;
          }}
        />
      );

    return (
      <img
        className="profile-banner"
        src={`${assetsURI}/${user?.banner}`}
        onError={({ target }) => {
          target.src = backgroundImage;
        }}
      />
    );
  };

  if (edit) {
    return (
      <ProfileContext.Provider value={contextValues}>
        <Edit tab={0} />
      </ProfileContext.Provider>
    );
  }

  return (
    <ProfileContext.Provider value={contextValues}>
      <ScrollToTop />
      <div className="profile">
        {viewMode === "guest" && (
          <div className="profile-guest-mode">
            <span>
              {Translation("You're currently viewing your profile as a guest")}
            </span>
            <span
              className="profile-guest-mode__exit"
              onClick={handleExitGuestModeClick}
            >
              {Translation("Exit guest mode")}
            </span>
          </div>
        )}
        {getBanner()}
        <div className="profile-container">
          <ProfileCard />
          <div className="profile-content">
            <HeaderFollowing />
            <div className="profile-content-tabs-scroller">
              <div className="profile-content-tabs">
                {tabs?.map(({ name, value }) => {
                  const classNames = ["profile-content-tabs-tab"];

                  if (tabSelected === value)
                    classNames.push("profile-content-tabs-tab--active");

                  return (
                    <div
                      key={value}
                      className={classNames.join(" ")}
                      onClick={() => handleTabClick(value)}
                    >
                      {Translation(name)}
                    </div>
                  );
                })}
              </div>
            </div>
            {getContent()}
          </div>
        </div>
      </div>
    </ProfileContext.Provider>
  );
};

Profile.propTypes = {
  id: PropTypes.string,
  tab: PropTypes.string,
  onChangeTab: PropTypes.func,
  edit: PropTypes.bool,
  onEdit: PropTypes.func,
};

Profile.defaultProps = {
  tab: "projects",
  onChangeTab: () => {},
  edit: false,
  onEdit: () => {},
};

export default Profile;
